import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  fetchOrders,
  setFilters,
  clearError,
  resetState,
  setSelectedOrder,
  assignWorkerToOrder,
  cancelOrder,
  bulkAssignOrders,
  bulkCancelOrders,
  deassignWorkerFromOrder
} from '../store/slices/orderManagementSlice';
import { fetchUsers } from '../store/slices/userManagementSlice';
import { Order } from '../types/order';
import OrderUpload from '../components/orders/OrderUpload';
import OrderTable from '../components/orders/OrderTable';
import OrderAssignmentModal from '../components/orders/OrderAssignmentModal';
import BulkAssignmentModal from '../components/orders/BulkAssignmentModal';
import { 
  Search, 
  Filter, 
  RefreshCw,
  Upload,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Download,
  User as UserIcon,
  X,
  UserMinus
} from 'lucide-react';

const OrderManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { 
    orders, 
    loading, 
    error, 
    pagination, 
    filters 
  } = useAppSelector((state) => state.orderManagement);
  
  const { users: workers } = useAppSelector((state) => state.userManagement);

  // Local state for form inputs
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedWorkerId, setSelectedWorkerId] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedOrderForAssignment, setSelectedOrderForAssignment] = useState<Order | null>(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [showCancelConfirm, setShowCancelConfirm] = useState<Order | null>(null);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showBulkCancelConfirm, setShowBulkCancelConfirm] = useState(false);
  const [showDeassignConfirm, setShowDeassignConfirm] = useState<Order | null>(null);

  // Initialize local state from Redux filters
  useEffect(() => {
    setSearchTerm(filters.search || '');
    setSelectedStatus(filters.status || '');
    setSelectedDate(filters.date || '');
    setSelectedWorkerId(filters.worker_id?.toString() || '');
  }, []);

  useEffect(() => {
    dispatch(fetchOrders(filters));
    dispatch(fetchUsers({ page: 1, limit: 100 }));
    
    return () => {
      dispatch(resetState());
    };
  }, [dispatch, filters]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const newFilters = {
        ...filters,
        search: searchTerm.trim() || undefined,
        status: selectedStatus || undefined,
        date: selectedDate || undefined,
        worker_id: selectedWorkerId ? parseInt(selectedWorkerId) : undefined,
        page: 1
      };
      
      // Only update if filters actually changed
      const filtersChanged = 
        newFilters.search !== filters.search ||
        newFilters.status !== filters.status ||
        newFilters.date !== filters.date ||
        newFilters.worker_id !== filters.worker_id;
      
      if (filtersChanged) {
        dispatch(setFilters(newFilters));
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedStatus, selectedDate, selectedWorkerId, dispatch, filters]);

  const handleSearch = () => {
    const newFilters = {
      ...filters,
      search: searchTerm.trim() || undefined,
      status: selectedStatus || undefined,
      date: selectedDate || undefined,
      worker_id: selectedWorkerId ? parseInt(selectedWorkerId) : undefined,
      page: 1
    };
    dispatch(setFilters(newFilters));
  };

  const handleReset = () => {
    setSearchTerm('');
    setSelectedStatus('');
    setSelectedDate('');
    setSelectedWorkerId('');
    dispatch(setFilters({ page: 1, limit: 10 }));
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const removeFilter = (filterKey: string) => {
    switch (filterKey) {
      case 'search':
        setSearchTerm('');
        break;
      case 'status':
        setSelectedStatus('');
        break;
      case 'date':
        setSelectedDate('');
        break;
      case 'worker':
        setSelectedWorkerId('');
        break;
    }
  };

  // Get available workers for filter dropdown
  const availableWorkers = workers.filter(worker => 
    worker.role === 'worker' && worker.is_active
  );

  // Count active filters
  const activeFiltersCount = [searchTerm, selectedStatus, selectedDate, selectedWorkerId]
    .filter(Boolean).length;

  const handleOrderSelect = (order: Order) => {
    dispatch(setSelectedOrder(order));
    // Could open a modal or navigate to order details
  };

  const handleUploadComplete = () => {
    setShowUpload(false);
    dispatch(fetchOrders(filters));
  };
  const handlePageChange = (page: number) => {
    dispatch(setFilters({ ...filters, page }));
  };

  const handlePerPageChange = (perPage: number) => {
    dispatch(setFilters({ ...filters, page: 1, limit: perPage }));
  };

  const handleExportOrders = () => {
    // Export functionality would be implemented here
    alert('Export functionality will be implemented');
  };

  const handleAssignOrder = (order: Order) => {
    setSelectedOrderForAssignment(order);
    setShowAssignmentModal(true);
  };
  const handleAssignWorker = async (orderId: number, workerId: number) => {
    try {
      await dispatch(assignWorkerToOrder({ orderId, workerId })).unwrap();
      setShowAssignmentModal(false);
      setSelectedOrderForAssignment(null);
      // Refresh orders list to show updated assignment
      dispatch(fetchOrders(filters));
    } catch (error) {
      console.error('Assignment failed:', error);
    }
  };

  const handleCancelOrder = (order: Order) => {
    setShowCancelConfirm(order);
  };

  const handleConfirmCancel = async () => {
    if (showCancelConfirm) {
      try {
        await dispatch(cancelOrder(showCancelConfirm.id)).unwrap();
        setShowCancelConfirm(null);
      } catch (error) {
        console.error('Cancel failed:', error);
      }
    }
  };
  const handleBulkAssign = async (workerId: number) => {
    try {
      await dispatch(bulkAssignOrders({ orderIds: selectedOrderIds, workerId })).unwrap();
      setSelectedOrderIds([]);
      setShowBulkAssignModal(false);
      // Refresh orders list to show updated assignments
      dispatch(fetchOrders(filters));
    } catch (error) {
      console.error('Bulk assignment failed:', error);
    }
  };

  const handleBulkCancel = async () => {
    try {
      await dispatch(bulkCancelOrders(selectedOrderIds)).unwrap();
      setSelectedOrderIds([]);
      setShowBulkCancelConfirm(false);
    } catch (error) {
      console.error('Bulk cancel failed:', error);
    }
  };

  const handleDeassignOrder = (order: Order) => {
    setShowDeassignConfirm(order);
  };

  const handleConfirmDeassign = async () => {
    if (showDeassignConfirm) {
      try {
        await dispatch(deassignWorkerFromOrder(showDeassignConfirm.id)).unwrap();
        setShowDeassignConfirm(null);
      } catch (error) {
        console.error('Deassign failed:', error);
      }
    }
  };

  const handleBulkDeassign = async () => {
    try {
      const results = await Promise.allSettled(
        selectedOrderIds.map(orderId => 
          dispatch(deassignWorkerFromOrder(orderId)).unwrap()
        )
      );

      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      setSelectedOrderIds([]);
      setShowBulkAssignModal(false);
      
      // You could show a toast notification here
      console.log(`${successful} orders deassigned successfully. ${failed > 0 ? `${failed} failed.` : ''}`);
    } catch (error) {
      console.error('Bulk deassign failed:', error);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Order Management</h1>
        <p className="mt-2 text-gray-600">Upload and manage daily orders from Shopify</p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Upload Section */}
      {showUpload && (
        <div className="mb-8 bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Upload Orders</h2>
            <button
              onClick={() => setShowUpload(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          </div>
          <OrderUpload onUploadComplete={handleUploadComplete} />
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search Input */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Orders
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                placeholder="Search by order number, SKU, or style name..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="assigned">Assigned</option>
              <option value="picking">Picking</option>
              <option value="picked">Picked</option>
              <option value="verified">Verified</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Worker Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assigned Worker
            </label>
            <select
              value={selectedWorkerId}
              onChange={(e) => setSelectedWorkerId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Workers</option>
              <option value="unassigned">Unassigned</option>
              {availableWorkers.map(worker => (
                <option key={worker.id} value={worker.id.toString()}>
                  {worker.full_name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Order Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={handleSearch}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Filter className="h-4 w-4 mr-2" />
              Apply Filters
            </button>
            
            {activeFiltersCount > 0 && (
              <button
                onClick={handleReset}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Clear All ({activeFiltersCount})
              </button>
            )}
          </div
          >
          {loading && (
            <div className="flex items-center text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
              Searching...
            </div>
          )}
        </div>
        
        {/* Active Filters Display */}
        {activeFiltersCount > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-sm text-gray-500">Active filters:</span>
            
            {searchTerm && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Search: "{searchTerm}"
                <button
                  onClick={() => removeFilter('search')}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            
            {selectedStatus && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Status: {selectedStatus}
                <button
                  onClick={() => removeFilter('status')}
                  className="ml-2 text-green-600 hover:text-green-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            
            {selectedWorkerId && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Worker: {selectedWorkerId === 'unassigned' 
                  ? 'Unassigned' 
                  : availableWorkers.find(w => w.id.toString() === selectedWorkerId)?.full_name || 'Unknown'
                }
                <button
                  onClick={() => removeFilter('worker')}
                  className="ml-2 text-purple-600 hover:text-purple-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            
            {selectedDate && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                Date: {new Date(selectedDate).toLocaleDateString()}
                <button
                  onClick={() => removeFilter('date')}
                  className="ml-2 text-orange-600 hover:text-orange-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Action Bar */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-700">
            Showing {orders.length} of {pagination.total_records} orders
          </div>
          {selectedOrderIds.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {selectedOrderIds.length} selected
              </span>
              <button
                onClick={() => setShowBulkAssignModal(true)}
                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <UserIcon className="h-3 w-3 mr-1" />
                Bulk Assign
              </button>
              <button
                onClick={() => setShowBulkCancelConfirm(true)}
                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <X className="h-3 w-3 mr-1" />
                Bulk Cancel
              </button>
            </div>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleExportOrders}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Orders
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <OrderTable
        orders={orders}
        onOrderSelect={handleOrderSelect}
        onAssignOrder={handleAssignOrder}
        onDeassignOrder={handleDeassignOrder}
        onCancelOrder={handleCancelOrder}
        loading={loading}
        selectedOrderIds={selectedOrderIds}
        onOrderSelectionChange={setSelectedOrderIds}
      />

      {/* Pagination */}      {pagination.total_pages > 1 && (
        <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
          {/* Mobile pagination */}
          <div className="flex-1 flex flex-col space-y-3 sm:hidden">
            <div className="flex justify-between">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.total_pages}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            
            {/* Mobile per-page selector */}
            <div className="flex items-center justify-center space-x-2">
              <label htmlFor="per-page-mobile" className="text-sm text-gray-700">
                Show:
              </label>
              <select
                id="per-page-mobile"
                value={pagination.per_page}
                onChange={(e) => handlePerPageChange(Number(e.target.value))}
                className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-700">per page</span>
            </div>
            
            <div className="text-center">
              <p className="text-sm text-gray-700">
                Page {pagination.current_page} of {pagination.total_pages} ({pagination.total_records} results)
              </p>
            </div>
          </div><div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div className="flex items-center space-x-4">
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {(pagination.current_page - 1) * pagination.per_page + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total_records)}
                </span>{' '}
                of{' '}
                <span className="font-medium">{pagination.total_records}</span>{' '}
                results
              </p>
              
              {/* Per-page selector */}
              <div className="flex items-center space-x-2">
                <label htmlFor="per-page" className="text-sm text-gray-700">
                  Show:
                </label>
                <select
                  id="per-page"
                  value={pagination.per_page}
                  onChange={(e) => handlePerPageChange(Number(e.target.value))}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-700">per page</span>
              </div>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                {[...Array(pagination.total_pages)].map((_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === pagination.current_page
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.total_pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Order Assignment Modal */}
      <OrderAssignmentModal
        order={selectedOrderForAssignment}
        workers={workers}
        isOpen={showAssignmentModal}
        onClose={() => {
          setShowAssignmentModal(false);
          setSelectedOrderForAssignment(null);
        }}
        onAssign={handleAssignWorker}
        loading={loading}
      />

      {/* Bulk Assignment Modal */}
      <BulkAssignmentModal
        orderCount={selectedOrderIds.length}
        workers={workers}
        isOpen={showBulkAssignModal}
        onClose={() => setShowBulkAssignModal(false)}
        onAssign={handleBulkAssign}
        onDeassign={handleBulkDeassign}
        loading={loading}
        hasAssignedOrders={selectedOrderIds.some(id => {
          const order = orders.find(o => o.id === id);
          return order?.assigned_worker_id;
        })}
      />

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Cancel Order</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to cancel order <strong>{showCancelConfirm.order_number}</strong>? 
                  This action cannot be undone.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowCancelConfirm(null)}
                    className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    Keep Order
                  </button>
                  <button
                    onClick={handleConfirmCancel}
                    className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Cancel Order
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Cancel Confirmation Modal */}
      {showBulkCancelConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Cancel Multiple Orders</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to cancel <strong>{selectedOrderIds.length}</strong> selected {selectedOrderIds.length === 1 ? 'order' : 'orders'}? 
                  This action cannot be undone.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowBulkCancelConfirm(false)}
                    disabled={loading}
                    className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
                  >
                    Keep Orders
                  </button>
                  <button
                    onClick={handleBulkCancel}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Cancelling...
                      </>
                    ) : (
                      'Cancel Orders'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deassign Confirmation Modal */}
      {showDeassignConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100">
                <UserMinus className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Deassign Worker</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to deassign the worker from order <strong>{showDeassignConfirm.order_number}</strong>? 
                  The order will return to pending status.
                </p>
                <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                  <div className="flex items-center">
                    <UserIcon className="h-4 w-4 text-orange-600 mr-2" />
                    <div className="text-sm">
                      <p className="font-medium text-orange-800">Currently assigned to:</p>
                      <p className="text-orange-700">{showDeassignConfirm.assigned_worker_name}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowDeassignConfirm(null)}
                    disabled={loading}
                    className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
                  >
                    Keep Assignment
                  </button>
                  <button
                    onClick={handleConfirmDeassign}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 bg-orange-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Deassigning...
                      </>
                    ) : (
                      'Deassign Worker'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
